import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  numeric,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status/type constants
// ---------------------------------------------------------------------------

export const PROPOSAL_STATUSES = ["draft", "sent", "viewed", "signed", "declined", "expired"] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

export const SECTION_TYPES = ["intro", "services", "pricing", "terms", "signature", "custom"] as const;
export type SectionType = (typeof SECTION_TYPES)[number];

export const SIGNATURE_REQUEST_STATUSES = ["pending", "sent", "viewed", "signed", "declined", "expired"] as const;
export type SignatureRequestStatus = (typeof SIGNATURE_REQUEST_STATUSES)[number];

export const SIGNATURE_METHODS = ["drawn", "typed", "uploaded"] as const;
export type SignatureMethod = (typeof SIGNATURE_METHODS)[number];

// ---------------------------------------------------------------------------
// JSONB type interfaces
// ---------------------------------------------------------------------------

export interface ProposalBrandConfig {
  logoUrl?: string;
  primaryColor?: string;
  fontFamily?: string;
  [key: string]: unknown;
}

export interface ProposalSectionContent {
  blocks?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface PricingLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// 1. Proposal Templates
// ---------------------------------------------------------------------------

export const proposalTemplates = pgTable("proposal_templates", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  sections: jsonb("sections").$type<Array<Record<string, unknown>>>().notNull().default([]),
  brandConfig: jsonb("brand_config").$type<ProposalBrandConfig>().notNull().default({}),
  industry: text("industry"), // For marketplace template sharing (F16)
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_proposal_tpl_tenant").on(table.tenantId),
]);

// ---------------------------------------------------------------------------
// 2. Proposals
// ---------------------------------------------------------------------------

export const proposals = pgTable("proposals", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  templateId: uuid("template_id"), // Nullable: standalone proposals possible
  dealId: uuid("deal_id"), // Nullable: proposal → pipeline integration (D.9)
  personId: uuid("person_id").notNull(), // Who receives the proposal
  title: text("title").notNull(),
  status: text("status").notNull().default("draft"), // ProposalStatus
  totalValue: numeric("total_value", { precision: 12, scale: 2 }),
  currency: text("currency").notNull().default("EUR"),
  // SC-10: Content hash — SHA-256 of all sections at send time. Proves content not modified post-send.
  contentHash: text("content_hash"),
  validUntil: timestamp("valid_until", tz),
  sentAt: timestamp("sent_at", tz),
  viewedAt: timestamp("viewed_at", tz),
  signedAt: timestamp("signed_at", tz),
  declinedAt: timestamp("declined_at", tz),
  // SD.1: Good/better/best option tiers for service proposals
  optionTiers: jsonb("option_tiers").$type<Array<{
    id: string;
    name: string;
    description?: string;
    totalValueCents: number;
    isRecommended?: boolean;
  }>>(),
  selectedOptionId: text("selected_option_id"), // which tier the client chose
  approvalMethod: text("approval_method"), // e_signature, verbal, online
  // C94: Offer builder extensions
  offerType: text("offer_type").notNull().default("proposal"), // proposal/quote/invoice/package/estimate/coupon/bundle/gift_card/subscription
  trackingToken: text("tracking_token"),
  viewCount: integer("view_count").notNull().default(0),
  aiGenerated: text("ai_generated").default("false"), // false/draft/approved — EU AI Act tracking
  paymentLinkUrl: text("payment_link_url"),
  paymentStatus: text("payment_status").notNull().default("unpaid"), // unpaid/partial/paid
  expiryNotificationSent: text("expiry_notification_sent").default("false"), // false/true — prevent double-send

  createdBy: uuid("created_by"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_proposals_tenant").on(table.tenantId),
  index("idx_proposals_status").on(table.tenantId, table.status),
  index("idx_proposals_deal").on(table.tenantId, table.dealId),
  index("idx_proposals_person").on(table.tenantId, table.personId),
  index("idx_proposals_valid_until").on(table.tenantId, table.validUntil),
  uniqueIndex("idx_proposals_tracking_token").on(table.trackingToken),
  index("idx_proposals_payment_status").on(table.tenantId, table.paymentStatus),
]);

// ---------------------------------------------------------------------------
// 3. Proposal Sections
// ---------------------------------------------------------------------------

export const proposalSections = pgTable("proposal_sections", {
  id: uuid("id").primaryKey().defaultRandom(),
  proposalId: uuid("proposal_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  sectionType: text("section_type").notNull(), // SectionType
  title: text("title").notNull(),
  content: jsonb("content").$type<ProposalSectionContent>().notNull().default({}),
  pricing: jsonb("pricing").$type<PricingLineItem[]>(), // If sectionType = "pricing"
  displayOrder: integer("display_order").notNull().default(0),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_proposal_sec_tenant").on(table.tenantId),
  index("idx_proposal_sec_proposal").on(table.tenantId, table.proposalId),
]);

// ---------------------------------------------------------------------------
// 4. Signature Requests
// ---------------------------------------------------------------------------
// SC-1: tokenHash stores HMAC-SHA256 hash of capability token. Raw token exists
// ONLY in the signing URL sent to the signer. Never logged, never persisted raw.
// Lookup: hash the incoming token with SIGNATURE_TOKEN_HASH_SECRET, query by hash.

export const signatureRequests = pgTable("signature_requests", {
  id: uuid("id").primaryKey().defaultRandom(),
  proposalId: uuid("proposal_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  // SC-1: Capability token — HMAC-SHA256 hash stored, raw token NEVER persisted
  tokenHash: text("token_hash").notNull().unique(),
  // SC-2: PII fields encrypted via @ease/crypto KEK/DEK
  signerEmail: text("signer_email").notNull(), // Encrypted at rest
  signerEmailHash: text("signer_email_hash").notNull(), // HMAC for lookup
  signerName: text("signer_name").notNull(), // Encrypted at rest
  encryptionKeyId: text("encryption_key_id"), // KEK version used for PII encryption
  status: text("status").notNull().default("pending"), // SignatureRequestStatus
  expiresAt: timestamp("expires_at", tz).notNull(),
  viewedAt: timestamp("viewed_at", tz),
  signedAt: timestamp("signed_at", tz),
  declinedAt: timestamp("declined_at", tz),
  reminderSentAt: timestamp("reminder_sent_at", tz),
  reminderCount: integer("reminder_count").notNull().default(0),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_sig_req_tenant").on(table.tenantId),
  index("idx_sig_req_proposal").on(table.tenantId, table.proposalId),
  index("idx_sig_req_status").on(table.tenantId, table.status),
  index("idx_sig_req_signer_email").on(table.tenantId, table.signerEmailHash),
  index("idx_sig_req_expires").on(table.tenantId, table.expiresAt), // F-1: Cron expiration scan
  uniqueIndex("uq_sig_req_proposal_signer").on(table.proposalId, table.signerEmailHash),
]);

// ---------------------------------------------------------------------------
// 5. Signatures — APPEND-ONLY LEGAL RECORDS
// ---------------------------------------------------------------------------
// IMMUTABILITY: This table MUST be append-only at the service layer.
// No UPDATE, no DELETE. Once a signature is captured, it is a legal record.
// GDPR erasure: Anonymize PII fields (signerEmail/Name/userAgent → anonymization token)
// but RETAIN signatureData + legalText during legal retention period.
//
// SC-3: signatureData is biometric data (GDPR Art. 9) — encrypted via KEK/DEK.
// SC-4: retentionExpiresAt + legalHoldId support legal retention vs GDPR erasure.
// SC-5: legalTextHash (SHA-256) detects post-signature tampering.
// SC-6: evidencePackageR2Key stores the eIDAS-compliant evidence bundle.

export const signatures = pgTable("signatures", {
  id: uuid("id").primaryKey().defaultRandom(),
  proposalId: uuid("proposal_id").notNull(),
  signatureRequestId: uuid("signature_request_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  // SC-3: Biometric data (GDPR Art. 9) — encrypted at rest via @ease/crypto KEK/DEK
  signatureData: text("signature_data").notNull(), // Encrypted base64 SVG path
  // SC-11: eIDAS classification: drawn (canvas), typed (name), uploaded (image)
  signatureMethod: text("signature_method").notNull(), // SignatureMethod
  // SC-5: Legal text frozen at sign time + tamper detection hash
  legalText: text("legal_text").notNull(), // Exact consent text shown to signer
  legalTextHash: text("legal_text_hash").notNull(), // SHA-256 content integrity hash
  legalTextVersion: text("legal_text_version"), // Which terms version (e.g., "terms-v2.3")
  // Legal evidence fields (eIDAS + ESIGN Act)
  ipAddressHash: text("ip_address_hash").notNull(), // HMAC hash — NEVER raw IP (GDPR)
  userAgent: text("user_agent"), // PII (fingerprinting) but legal evidence — MUST anonymize during GDPR erasure
  signedAt: timestamp("signed_at", tz).notNull(), // Server-side only — NEVER client-dictated
  // Storage references
  signedDocumentR2Key: text("signed_document_r2_key"), // R2 path to signed PDF
  evidencePackageR2Key: text("evidence_package_r2_key"), // SC-6: eIDAS evidence bundle (JSON/PDF)
  // Encryption
  encryptionKeyId: text("encryption_key_id"), // KEK version for signatureData encryption
  // SC-4: GDPR erasure vs legal retention
  retentionExpiresAt: timestamp("retention_expires_at", tz), // Legal retention period (default: signedAt + 10 years)
  legalHoldId: uuid("legal_hold_id"), // FK to legal_holds — prevents erasure during hold
  anonymizedAt: timestamp("anonymized_at", tz), // When PII was stripped (partial erasure)
  // Metadata
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_signatures_tenant").on(table.tenantId),
  index("idx_signatures_proposal").on(table.tenantId, table.proposalId),
  index("idx_signatures_request").on(table.tenantId, table.signatureRequestId),
]);
